import React, { useState } from 'react';
import './UIComponentsScreen.css';

// Import UI Components
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Textarea from '../../components/ui/Textarea/Textarea';
import Select from '../../components/ui/Select/Select';
import Checkbox from '../../components/ui/Checkbox/Checkbox';
import Radio from '../../components/ui/Radio/Radio';
import Switch from '../../components/ui/Switch/Switch';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import Card from '../../components/ui/Card/Card';
import Popover from '../../components/ui/Popover/Popover';
import { useToast } from '../../contexts/ToastContext';

const UIComponentsScreen = () => {
  // State for form inputs
  const [inputValue, setInputValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [radioValue, setRadioValue] = useState('option1');
  const [switchChecked, setSwitchChecked] = useState(false);
  const [dateValue, setDateValue] = useState(null);

  // Toast hook
  const { showToast, showSuccess, showError, showWarning, showInfo, showLoading } = useToast();

  // Sample options for Select component
  const selectOptions = [
    { value: 'option1', label: 'Option 1' },
    { value: 'option2', label: 'Option 2' },
    { value: 'option3', label: 'Option 3' },
  ];

  return (
    <div className="ui-components-screen">
      <h1 style={{ fontWeight: 900, letterSpacing: '1px', fontSize: '2rem' }}>UI Components</h1>
      
      <div className="components-grid">
        {/* Button Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Button Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <h4>Basic Variants</h4>
              <div className="button-row">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="success">Success</Button>
                <Button variant="warning">Warning</Button>
                <Button variant="danger">Danger</Button>
              </div>
              
              <h4>Outline & Ghost</h4>
              <div className="button-row">
                <Button variant="outline">Outline</Button>
                <Button variant="outline-secondary">Outline Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="glass">Glass</Button>
              </div>
              
              <h4>Size Variants</h4>
              <div className="button-row">
                <Button variant="primary" size="small">Small</Button>
                <Button variant="primary" size="medium">Medium</Button>
                <Button variant="primary" size="large">Large</Button>
              </div>
              
              <h4>Interactive States</h4>
              <div className="button-row">
                <Button variant="primary" loading>Loading</Button>
                <Button variant="primary" ripple>Ripple Effect</Button>
                <Button variant="secondary" disabled>Disabled</Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Input Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Input Component</h3>
            <div style={{ padding: '0 20px 16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Input
                placeholder="Enter text here..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <Input
                type="email"
                placeholder="Enter email..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <Input
                type="password"
                placeholder="Enter password..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </div>
          </div>
        </Card>

        {/* Textarea Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Textarea Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <Textarea
                placeholder="Enter your message here..."
                value={textareaValue}
                onChange={(e) => setTextareaValue(e.target.value)}
                rows={4}
              />
            </div>
          </div>
        </Card>

        {/* Select Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Select Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <Select
                options={selectOptions}
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                placeholder="Choose an option..."
              />
            </div>
          </div>
        </Card>

        {/* Checkbox Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Checkbox Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <Checkbox
                checked={checkboxChecked}
                onChange={(e) => setCheckboxChecked(e.target.checked)}
                label="Accept terms and conditions"
              />
              <Checkbox
                checked={!checkboxChecked}
                onChange={(e) => setCheckboxChecked(!e.target.checked)}
                label="Subscribe to newsletter"
              />
            </div>
          </div>
        </Card>

        {/* Radio Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Radio Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <Radio
                name="radioGroup"
                value="option1"
                checked={radioValue === 'option1'}
                onChange={(e) => setRadioValue(e.target.value)}
                label="Option 1"
              />
              <Radio
                name="radioGroup"
                value="option2"
                checked={radioValue === 'option2'}
                onChange={(e) => setRadioValue(e.target.value)}
                label="Option 2"
              />
              <Radio
                name="radioGroup"
                value="option3"
                checked={radioValue === 'option3'}
                onChange={(e) => setRadioValue(e.target.value)}
                label="Option 3"
              />
            </div>
          </div>
        </Card>

        {/* Switch Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Switch Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <Switch
                checked={switchChecked}
                onChange={(e) => setSwitchChecked(e.target.checked)}
                label="Enable notifications"
              />
              <div style={{ marginTop: '16px' }}></div>
              <Switch
                checked={!switchChecked}
                onChange={(e) => setSwitchChecked(!e.target.checked)}
                label="Dark mode"
              />
            </div>
          </div>
        </Card>

        {/* DatePicker Component */}
        <Card className="allow-overflow">
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>DatePicker Component</h3>
            <div style={{ padding: '0 20px 16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <DatePicker
                value={dateValue}
                onChange={setDateValue}
                placeholder="Select a date..."
              />
              <DatePicker
                value={dateValue}
                onChange={setDateValue}
                showTimeSelect
                dateFormat="MM/dd/yyyy h:mm aa"
                placeholder="Select date and time..."
              />
              <DatePicker
                value={dateValue}
                onChange={setDateValue}
                minDate={new Date()}
                placeholder="Select future date..."
              />

            </div>
          </div>
        </Card>

        {/* Popover Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Popover Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <h4>Hover Trigger</h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <Popover 
                  content="This is a top popover with hover trigger"
                  placement="top"
                  trigger="hover"
                >
                  <Button variant="primary">Hover Top</Button>
                </Popover>
                
                <Popover 
                  content="This is a bottom popover with hover trigger"
                  placement="bottom"
                  trigger="hover"
                >
                  <Button variant="primary">Hover Bottom</Button>
                </Popover>
                
                <Popover 
                  content="This is a left popover with hover trigger"
                  placement="left"
                  trigger="hover"
                >
                  <Button variant="primary">Hover Left</Button>
                </Popover>
                
                <Popover 
                  content="This is a right popover with hover trigger"
                  placement="right"
                  trigger="hover"
                >
                  <Button variant="primary">Hover Right</Button>
                </Popover>
              </div>
              
              <h4>Click Trigger</h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <Popover 
                  content={
                    <div>
                      <div className="popover-title">Click Popover</div>
                      <div className="popover-body">This popover is triggered by clicking the button.</div>
                      <div className="popover-footer">
                        <Button variant="outline" size="small">Cancel</Button>
                        <Button variant="primary" size="small">OK</Button>
                      </div>
                    </div>
                  }
                  placement="top"
                  trigger="click"
                >
                  <Button variant="secondary">Click for Popover</Button>
                </Popover>
              </div>
              
              <h4>Variants</h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Popover 
                  content="Info popover with blue accent"
                  placement="top"
                  trigger="hover"
                  className="info"
                >
                  <Button variant="outline">Info</Button>
                </Popover>
                
                <Popover 
                  content="Success popover with green accent"
                  placement="top"
                  trigger="hover"
                  className="success"
                >
                  <Button variant="success">Success</Button>
                </Popover>
                
                <Popover 
                  content="Warning popover with yellow accent"
                  placement="top"
                  trigger="hover"
                  className="warning"
                >
                  <Button variant="warning">Warning</Button>
                </Popover>
                
                <Popover 
                  content="Danger popover with red accent"
                  placement="top"
                  trigger="hover"
                  className="danger"
                >
                  <Button variant="danger">Danger</Button>
                </Popover>
              </div>
            </div>
          </div>
        </Card>

        {/* Toast Component */}
        <Card>
          <div className="component-example">
            <h3 style={{ margin: '0 0 16px 0', padding: '8px 20px 0 20px', fontSize: '18px', fontWeight: '600', color: '#333' }}>Toast Component</h3>
            <div style={{ padding: '0 20px 16px 20px' }}>
              <h4>Basic Toast Types</h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <Button 
                  variant="primary" 
                  onClick={() => showToast("This is a basic toast message")}
                >
                  Basic Toast
                </Button>
                <Button 
                  variant="success" 
                  onClick={() => showSuccess("Operation completed successfully!")}
                >
                  Success Toast
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => showError("Something went wrong!")}
                >
                  Error Toast
                </Button>
                <Button 
                  variant="warning" 
                  onClick={() => showWarning("Please check your input")}
                >
                  Warning Toast
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => showInfo("Here's some useful information")}
                >
                  Info Toast
                </Button>
              </div>
              
              <h4>Advanced Toast Examples</h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <Button 
                  variant="primary" 
                  onClick={() => showToast("Event has been created", {
                    description: "Sunday, December 03, 2023 at 9:00 AM",
                    action: {
                      label: "Undo",
                      onClick: () => showInfo("Undo action clicked!")
                    },
                  })}
                >
                  Toast with Action
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    const loadingToast = showLoading("Processing your request...");
                    setTimeout(() => {
                      showSuccess("Request completed successfully!");
                    }, 2000);
                  }}
                >
                  Loading Toast
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => showToast("Custom styled toast", {
                    style: {
                      background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none'
                    }
                  })}
                >
                  Custom Styled
                </Button>
              </div>
              
              <h4>Toast with Rich Content</h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button 
                  variant="primary" 
                  onClick={() => showSuccess("User Profile Updated", {
                    description: "Your profile information has been successfully updated and saved to the database.",
                    duration: 5000,
                  })}
                >
                  Rich Success
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => showError("Failed to Save Changes", {
                    description: "Unable to save your changes. Please check your internet connection and try again.",
                    action: {
                      label: "Retry",
                      onClick: () => showInfo("Retrying...")
                    },
                  })}
                >
                  Rich Error
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UIComponentsScreen; 